import PeopleIcon from "@mui/icons-material/People";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { semanticColors } from "./palette";
import { AttentionItem, StatCardData } from "./types";
import type {
  AttendanceByGrade,
  DailyFeeTrend,
  FacultyAttendance,
  FeeByGrade,
  MonthlyFeeTrend,
} from "../types";

// ---- Action Center ----
// Locked v1 scope: 4 cards only. Everything else is parked below (commented,
// not deleted) until we're ready to pull individual items back in behind
// "View All".
export const attentionItems: AttentionItem[] = [
  {
    id: "last-year-dues",
    severity: "critical",
    metric: "12",
    label: "Students with previous year dues",
    detail: "Impacts school operations.",
    actionLabel: "Review dues",
    route: "/app/fees/previous-year-dues",
  },
  {
    id: "low-attendance-students",
    severity: "warning",
    metric: "8",
    label: "Students below 75% attendance this month",
    detail: "Reduces school performance.",
    actionLabel: "View report",
    route: "/app/attendance/reports",
  },
  {
    id: "pending-admissions",
    severity: "warning",
    metric: "5",
    label: "Admission enquiries awaiting review",
    detail: "Oldest enquiry pending 4 days.",
    actionLabel: "Review enquiries",
    route: "/app/enquiries",
  },
  {
    id: "faculty-leave",
    severity: "info",
    metric: "3",
    label: "Faculty leave requests pending",
    detail: "Waiting for your approval.",
    actionLabel: "Review requests",
    route: "/app/staff/leave",
  },
];

// ---- Top stat cards (clickable + trend) ----
export const statCards: StatCardData[] = [
  {
    id: "total-students",
    heading: "Total Students",
    value: "2,450",
    subHeading: "Across all classes",
    icon: <PeopleIcon fontSize="small" />,
    accentColor: semanticColors.info,
    route: "/app/fees/summary",
    trendLabel: "+3.2% vs last month",
    trendDirection: "up",
    trendMeaning: "goodWhenUp",
  },
  {
    id: "admissions-in-progress",
    heading: "Admissions In Progress",
    value: "18",
    subHeading: "Processing",
    icon: <PersonAddAltIcon fontSize="small" />,
    accentColor: semanticColors.warning,
    route: "/app/enquiries",
    trendLabel: "-5 vs last week",
    trendDirection: "down",
    trendMeaning: "goodWhenDown",
  },
  {
    id: "attendance-today",
    heading: "Attendance Today",
    value: "94.2%",
    subHeading: "Overall presence",
    icon: <FactCheckIcon fontSize="small" />,
    accentColor: semanticColors.success,
    route: "/app/attendance/reports",
    trendLabel: "+1.1% vs yesterday",
    trendDirection: "up",
    trendMeaning: "goodWhenUp",
  },
  {
    id: "fee-collected",
    heading: "Fee Collected",
    value: "₹42.80 L",
    subHeading: "2025–26 overall",
    icon: <PaymentsIcon fontSize="small" />,
    accentColor: semanticColors.success,
    route: "/app/fees/receipts",
    trendLabel: "+12% vs last month",
    trendDirection: "up",
    trendMeaning: "goodWhenUp",
  },
  {
    id: "current-year-dues",
    heading: "C.Year Dues",
    value: "₹8.60 L",
    subHeading: "2025–26",
    icon: <AccountBalanceWalletIcon fontSize="small" />,
    accentColor: semanticColors.warning,
    route: "/app/fees/outstanding",
    trendLabel: "-4% vs last month",
    trendDirection: "down",
    trendMeaning: "goodWhenDown",
  },
  {
    id: "previous-year-dues",
    heading: "P.Year Dues",
    value: "₹2.10 L",
    subHeading: "Overdue",
    icon: <WarningAmberIcon fontSize="small" />,
    accentColor: semanticColors.critical,
    route: "/app/fees/outstanding",
    trendLabel: "No change",
    trendDirection: "flat",
    trendMeaning: "goodWhenDown",
  },
];

// ---- Charts: reuse the existing chart components, feed static data ----
export const feeByGrade: FeeByGrade[] = [
  { grade: "1", collected: 320000, due: 40000 },
  { grade: "2", collected: 298000, due: 52000 },
  { grade: "3", collected: 341000, due: 31000 },
  { grade: "4", collected: 305000, due: 47000 },
  { grade: "5", collected: 362000, due: 28000 },
  { grade: "6", collected: 289000, due: 61000 },
  { grade: "7", collected: 315000, due: 39000 },
  { grade: "8", collected: 274000, due: 58000 },
];

export const dailyFeeTrend: DailyFeeTrend[] = [
  { day: "1", collected: 42000 },
  { day: "5", collected: 61000 },
  { day: "10", collected: 38000 },
  { day: "15", collected: 74000 },
  { day: "20", collected: 55000 },
  { day: "25", collected: 68000 },
  { day: "30", collected: 47000 },
];

export const monthlyFeeTrend: MonthlyFeeTrend[] = [
  { month: "Apr", collected: 620000, discount: 18000 },
  { month: "May", collected: 580000, discount: 15000 },
  { month: "Jun", collected: 710000, discount: 22000 },
  { month: "Jul", collected: 690000, discount: 19000 },
  { month: "Aug", collected: 640000, discount: 17000 },
];

export const attendanceByGrade: AttendanceByGrade[] = [
  { grade: "1", sections: [{ name: "A", total: 3 }] },
  { grade: "2", sections: [{ name: "A", total: 1 }] },
  { grade: "3", sections: [{ name: "A", total: 4 }] },
  { grade: "6", sections: [{ name: "B", total: 6 }] },
  { grade: "7", sections: [{ name: "A", total: 5 }] },
  { grade: "8", sections: [{ name: "A", total: 2 }] },
];

export const facultyAttendance: FacultyAttendance[] = [
  { category: "FACULTY", attendance: 42 },
  { category: "Administrative Staff", attendance: 8 },
  { category: "Support Staff", attendance: 11 },
  { category: "Ground / Maintenance", attendance: 4 },
  { category: "Security", attendance: 3 },
];

export const adminDisplayName = "Admin";
